import { Ref, ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import Router from "@/router/v2";
import { Dilution, GroupTag, Site, Tank, TankGroup, TankMeasures, Task, User, VaccineConfiguration, } from "@/types/v2";
import { useTanksStore, useTasksStore } from "@/stores/v2";
import { TasksService } from "@/services/v2/tasks";
import QueueService from "@/services/v2/queue";

import { Row } from "@/interfaces/TableInterface";
import { isVaccinationTask, isEnvironmentalTask } from "@/stores/v2/tasks/utils";
import { ChallengeConfiguration } from "@/types/v2/task/challenge/configuration.types";

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const parseScheduledOn = (scheduledOn: string | null): Date | null => {
  if (!scheduledOn || typeof scheduledOn !== "string") {
    return null;
  }

  const normalized = scheduledOn.trim();
  if (!normalized) {
    return null;
  }

  if (DATE_ONLY_REGEX.test(normalized)) {
    const [year, month, day] = normalized.split("-").map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isTaskActive = (task: Task): boolean => {
  return task.status === "pending" || task.status === "in_progress";
};

const isFutureScheduledTask = (task: Task, now = new Date()): boolean => {
  const scheduledDate = parseScheduledOn(task.scheduledOn);
  if (!scheduledDate) {
    return false;
  }

  return scheduledDate.getTime() > now.getTime();
};

// Check if task is available to start
export const isTaskAvailable = (task: Task) => {
  return !isFutureScheduledTask(task);
};

// Button text function TODO: Not good approach need better solution
export const taskStartText = (
  currentUser: User | null,
  selectedTask: Task | null
) => {
  if (
    currentUser?.username !== selectedTask?.currentOwner &&
    selectedTask?.currentOwner !== null
  ) {
    return "Take over";
  }
  return "Start";
};

export const filterTasks = (filter: string): Task[] => {
  const tasksStore = useTasksStore();
  const tasks: Task[] = tasksStore.tasks;
  const now = new Date();

  if (filter === "pending") {
    return tasks.filter((task) => isTaskActive(task) && !isFutureScheduledTask(task, now));
  } else if (filter === "finished") {
    return tasks.filter(
      (task) =>
        task.status === "finished" &&
        (task.type === "regular" || task.type === "irregular")
    );
  } else if (filter === "scheduled") {
    return tasks.filter((task) => isTaskActive(task) && isFutureScheduledTask(task, now));
  } else {
    return tasks;
  }
};

// Task meta text
export const taskMetaText = (task: Task) => {
  let statusActionText = "";
  if (task.startedBy !== null && task.status !== "finished") {
    statusActionText = "Started by ";
  } else if (task.startedBy !== null && task.status === "finished") {
    statusActionText = "Completed by ";
  }

  return `${statusActionText} ${task.startedBy}`;
};

// Prepare task when starting it
export const prepareTaskForWork = (
  task: Task,
  currentUser: User | null
): Task => {
  const applyInProgressStatus = (task: Task): Task => {
    return {
      ...task,
      status: "in_progress",
    };
  };

  const setTaskOwner = (task: Task, owner: string): Task => {
    return {
      ...task,
      currentOwner: owner,
    };
  };
  const prepareTanksForTask = (taskKey: string): Tank[] => {
    const tanksStore = useTanksStore();
    return tanksStore.getTanksByTaskId(taskKey);
  };

  // Apply the status
  let preparedTask = applyInProgressStatus(task);
  // Set the owners
  preparedTask = setTaskOwner(preparedTask, currentUser?.username ?? "");
  // Prepare tanks data
  const additionalTaskData = prepareTanksForTask(task.key);
  // Final task
  return {
    ...preparedTask,
    progressData: { tanks: additionalTaskData },
  };
};

// Apply the task to state & save to index db
export const activateTaskInProgress = (task: Task) => {
  const tasksStore = useTasksStore();
  // Update task in indexDB
  tasksStore.setActiveTask(task);
};

// Task selection handling
export const useTaskSelection = (): {
  selectedTask: Ref<Task | null>;
  showModal: Ref<boolean>;
  handleSelectTask: (task: Task) => void;
} => {
  // Stores
  const tasksStore = useTasksStore();

  // Local state
  const selectedTask: Ref<Task | null> = ref(null);
  const showModal: Ref<boolean> = ref(false);

  // Task selection handler
  const handleSelectTask = (task: Task) => {
    // Set the active task object in store
    tasksStore.setSelectedTask(task);
    // Set local state active task
    selectedTask.value = task;
    // Toggle modal visibility
    showModal.value = !showModal.value;
  };

  return {
    selectedTask,
    showModal,
    handleSelectTask,
  };
};

// Vaccination configuration changes
export const handleConfigFieldChange = (
  configIndex: number,
  index: number,
  key: string,
  item: object
) => {
  const tasksStore = useTasksStore();
  tasksStore.updateConfigurationItem(configIndex, index, key, item);
};

// Add new vaccine to config
export const addNewVaccineToConfig = (configKey: string) => {
  const tasksStore = useTasksStore();

  const configurationId = configKey;
  const vaccineProperties = prepareEmptyVaccine();

  const newVaccine = tasksStore.addVaccineToConfig(
    configurationId,
    vaccineProperties
  );
  return newVaccine;
};

// Empty configuration
export const createEmptyConfiguration = () => {
  const tasksStore = useTasksStore();
  const lastConfigId = getHighestConfigId();
  const emptyVaccine = prepareEmptyVaccine();
  const emptyTank = prepareEmptyTank();
  const emptyTankGroup = prepareEmptyTankGroup();

  return tasksStore.createConfiguration(
    lastConfigId + 1,
    uuidv4(),
    [emptyVaccine],
    emptyTank,
    emptyTankGroup,
    0,
    emptyTank,
    emptyTankGroup
  );
};
// Empty configuration vaccine
export const prepareEmptyVaccine = (): any => {
  return {
    key: "",
    dose: 0,
    method: {},
    vaccine: {},
  };
};
// Empty tank
export const prepareEmptyTank = (): Tank => {
  return {
    key: "",
    label: "",
    room: "",
    name: "",
    required: false,
    groups: [],
    measures: prepareEmptyTankMeasures(),
  };
};
// Empty tank measures
export const prepareEmptyTankMeasures = (): TankMeasures => {
  return {
    salinity: { value: "", unit: "" },
    ammonia: { value: "", unit: "" },
    nitrite: { value: "", unit: "" },
    ph: { value: "", unit: "" },
    saturation: { value: "", unit: "" },
    temperature: { value: "", unit: "" },
  };
};

// Empty tank group
export const prepareEmptyTankGroup = (): TankGroup => {
  return {
    key: "",
    name: "",
    amountInTank: 0,
    deceased: 0,
    tags: [],
    progressData: [],
  };
};

// Empty group tag
export const prepareEmptyGroupTag = (): GroupTag => {
  return {
    key: "",
    title: "",
    isActive: false,
    name: "",
    textColor: "#fff",
    backgroundColor: "#e3e3e3",
  };
};

// Get highest vaccine configuration id
export const getHighestConfigId = (): number => {
  const TasksStore = useTasksStore();
  if (TasksStore.activeTask) {
    if (isVaccinationTask(TasksStore.activeTask)) {
      const configurations = TasksStore.activeTask?.details?.configurations;

      if (!configurations || configurations.length === 0) {
        return 0;
      }

      const highestConfigId = Math.max(...configurations.map((c: any) => c.id));

      return highestConfigId;
    }
  }

  return -1;
};

// Remove leading dash from color code
const removeLeadingHash = (color: string | undefined) => {
  if (color) {
    return color.startsWith("#") ? color.substring(1) : color;
  }

  // Return white by default
  return "#fff";
};

// Displaying for the Challenge process table
export const prepareChallengeTableData = (data: Array<any>) => {
  // TODO: Create helper functions to sort these things below - NEEDS TOTAL REVAMP BECAUSE THIS IS UGLY
  if (data && data.length > 0) {
    return data.map((config: any) => {
      let lastComment = "";
      // Extract last comment from configuration
      if (config.progressData) {
        const allComments =
          config.progressData
            ?.map((progressItem: any) => progressItem.comment)
            .filter((comment: any) => comment !== undefined) || [];
        lastComment = getLatestConfigComment(allComments);
      }
      const vaccinesText = config.vaccines
        .map(
          (vaccine: any) =>
            `<span>${vaccine.vaccine.name} (${vaccine.method.name}) ${vaccine.dose} mL</span>`
        )
        .join("<br>");

      const fromText = `${config.sourceTank.name} - ${config.sourceGroup.tags
        .map(
          (tag: any) => `
          <span style="display: inline-block; margin: 2px; font-weight: bold; padding: 2px 5px; font-size: 10px; text-align: center; color: #${removeLeadingHash(
            tag.textColor
          )}; border-radius: 5px; background-color: #${removeLeadingHash(
            tag.backgroundColor
          )};">${tag.title}</span>`
        )
        .join(" ")}`;

      const toText = `${config.targetTank.name} - ${config.sourceGroup.tags
        .map(
          (tag: any) => `
          <span style="display: inline-block; margin: 2px; font-weight: bold; padding: 2px 5px; font-size: 10px; text-align: center; color: #${removeLeadingHash(
            tag.textColor
          )}; border-radius: 5px; background-color: #${removeLeadingHash(
            tag.backgroundColor
          )};">${tag.title}</span>`
        )
        .join(" ")} ${
        config.selectedGroup
          ? config.selectedGroup.tags
              .map(
                (tag: any) =>
                  `<span style="display: inline-block; margin: 2px; font-weight: bold; padding: 2px 5px; font-size: 10px; text-align: center; color: #${removeLeadingHash(
                    tag.textColor
                  )}; border-radius: 5px; background-color: #${removeLeadingHash(
                    tag.backgroundColor
                  )};">${tag.title}</span>`
              )
              .join(" ")
          : ""
      }`;

      const numberOfFish = `${
        calculateTotalVaccinatedFish(config.progressData) ?? 0
      }/${config.targetAmount}`;
      // Calculate total vaccinated fish for the configuration
      const totalVaccinatedFish = calculateTotalVaccinatedFish(
        config.progressData
      );

      // Determine if the row should be colored based on your condition
      const shouldColorRow = totalVaccinatedFish >= config.targetAmount;

      return {
        id: config.id,
        Materials: vaccinesText,
        From: fromText,
        To: toText,
        "No. Fish": numberOfFish,
        Comment: lastComment,
        shouldColorRow: shouldColorRow,
      };
    });
  } else {
    return [];
  }
};
// Displaying for the vaccination process table
export const prepareVaccinationTableData = (data: Array<any>) => {
  // TODO: Create helper functions to sort these things below - NEEDS TOTAL REVAMP BECAUSE THIS IS UGLY
  if (data && data.length > 0) {
    return data.map((config: any) => {
      let lastComment = "";
      // Extract last comment from configuration
      if (config.progressData) {
        const allComments =
          config.progressData
            ?.map((progressItem: any) => progressItem.comment)
            .filter((comment: any) => comment !== undefined) || [];
        lastComment = getLatestConfigComment(allComments);
      }
      const vaccinesText = config.vaccines
        .map(
          (vaccine: any) =>
            `<span>${vaccine.vaccine.name} (${vaccine.method.name}) ${vaccine.dose} mL</span>`
        )
        .join("<br>");

      const fromText = `${config.sourceTank.name} - ${config.sourceGroup.tags
        .map(
          (tag: any) => `
          <span style="display: inline-block; margin: 2px; font-weight: bold; padding: 2px 5px; font-size: 10px; text-align: center; color: #${removeLeadingHash(
            tag.textColor
          )}; border-radius: 5px; background-color: #${removeLeadingHash(
            tag.backgroundColor
          )};">${tag.title}</span>`
        )
        .join(" ")}`;

      const toText = `${config.destinationTank.name} - ${config.sourceGroup.tags
        .map(
          (tag: any) => `
          <span style="display: inline-block; margin: 2px; font-weight: bold; padding: 2px 5px; font-size: 10px; text-align: center; color: #${removeLeadingHash(
            tag.textColor
          )}; border-radius: 5px; background-color: #${removeLeadingHash(
            tag.backgroundColor
          )};">${tag.title}</span>`
        )
        .join(" ")} ${config.destinationGroup.tags
        .map(
          (tag: any) => `
          <span style="display: inline-block; margin: 2px; font-weight: bold; padding: 2px 5px; font-size: 10px; text-align: center; color: #${removeLeadingHash(
            tag.textColor
          )}; border-radius: 5px; background-color: #${removeLeadingHash(
            tag.backgroundColor
          )};">${tag.title}</span>`
        )
        .join(" ")}`;

      const numberOfFish = `${
        calculateTotalVaccinatedFish(config.progressData) ?? 0
      }/${config.targetAmount}`;
      // Calculate total vaccinated fish for the configuration
      const totalVaccinatedFish = calculateTotalVaccinatedFish(
        config.progressData
      );

      // Determine if the row should be colored based on your condition
      const shouldColorRow = totalVaccinatedFish >= config.targetAmount;

      return {
        key: config.key,
        Vaccines: vaccinesText,
        From: fromText,
        To: toText,
        "No. Fish": numberOfFish,
        Comment: lastComment,
        shouldColorRow: shouldColorRow,
      };
    });
  } else {
    return [];
  }
};
// Sum total number of fish per configuration
const calculateTotalVaccinatedFish = (
  entries: Array<{ vaccinated: number; weight: number; comment: string }> = []
): number => {
  return entries.reduce((total, entry) => {
    if (entry.vaccinated && !isNaN(entry.vaccinated)) {
      return total + entry.vaccinated;
    }
    return total;
  }, 0);
};

// Return list of comments per configuration


// Get last comment on vaccination configuration
const getLatestConfigComment = (entries: Array<string> = []): string => {
  return `<span style="display: block; width: 100px;">${
    entries[entries.length - 1] ? entries[entries.length - 1] : ""
  }</span></br>`;
};

// Finish a task
export const finishTask = async (task: Task) => {
  const tasksStore = useTasksStore();
  const completedTask: Task = { ...task, status: "finished" };

  // Check network status
  if (!completedTask) {
    console.error("Task cannot be completed");
    return;
  }
  // Check if user has internet connection
  if (navigator.onLine) {
    try {
      // Send the completed task to the server
      await TasksService.finishTask(completedTask);
    } catch (error) {
      console.error("Error sending completed task to the server:", error);
      console.log("SENDING ASK TO QUEUE TASK TO QUEUE");
      // If there's an error, add the task to the offline queue
      await QueueService.addToQueue({ item: completedTask });
    }
  } else {
    // If offline, add the task to the offline queue
    console.log("ADDING TASK TO QUEUE");
    await QueueService.addToQueue({
      item: completedTask,
    });
  }

  tasksStore.completeTask(completedTask);
};

// Redirect to tasks view
export const redirectToTasksView = (): void => {
  const router = Router;
  router.push(`/dashboard/sites`);
};

// Find index of site
export const findSiteIndex = (allSites: Site[], activeSite: Site): number => {
  return allSites.findIndex((s) => s.key === activeSite.key);
};

// Find index for task
export const findTaskIndex = (tasks: Task[], targetTask: Task): number => {
  return tasks.findIndex((t) => t.key === targetTask.key);
};

// Clone tasks
export const cloneTasksArray = (tasks: Task[]): Task[] => {
  return [...tasks];
};

// Prepare modified site
export const createModifiedSite = (
  activeSite: Site,
  updatedTasks: Task[]
): Site => {
  return { ...activeSite, tasks: updatedTasks };
};

// Update all sites with modified site item
export const updateSiteInAllSites = (
  allSites: Site[],
  siteIndex: number,
  modifiedSite: Site
): void => {
  allSites[siteIndex] = modifiedSite;
};

// Save active site to index db
// export const storeModifiedSiteInIndexDB = async (
//   modifiedSite: Site
// ): Promise<void> => {
//   await SitesService.storeActiveSiteInIndexDB(modifiedSite);
// };

// Save all sites to index db
// export const storeAllSitesInIndexDB = async (
//   allSites: Site[]
// ): Promise<void> => {
//   await SitesService.storeSitesInIndexDB(allSites);
// };

// Get active site task related info
// export const getTaskSiteInfo = async (): Promise<{
//   activeSite: Site | undefined;
//   allSites: Site[];
//   siteIndex: number;
// }> => {
//   const activeSite = (await SitesService.getActiveSite()) as Site | null;

//   if (!activeSite) {
//     return { activeSite: undefined, allSites: [], siteIndex: -1 };
//   }

//   const allSites = await SitesService.getListOfSites();
//   const siteIndex = findSiteIndex(allSites, activeSite);

//   return { activeSite, allSites, siteIndex };
// };

export const getRevisionChangesPerItem = (entry: {
  vaccinated: string;
  weight: string;
  comment: string;
  deleted: boolean;
  revisions: Array<{
    vaccinated: number;
    weight: number;
    reason: string;
    deleted: boolean;
    timestamp: string;
  }>;
}) => {
  if (!entry || !entry.revisions || entry.revisions.length === 0) {
    return null;
  }

  const revisionsExtras: any = [];

  // Sort the revisions array based on the timestamp property
  const sortedRevisions = entry.revisions.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  for (let index = 0; index < sortedRevisions.length; index++) {
    const item = sortedRevisions[index];
    const extras = {
      index,
      newVaccinate:
        index === 0 ? entry.vaccinated : sortedRevisions[index - 1]?.vaccinated,
      oldVaccinate: item.vaccinated,
      newWeight:
        index === 0 ? entry.weight : sortedRevisions[index - 1]?.weight,
      oldWeight: item.weight,
      reason: item.reason,
      deleted: item.deleted ? true : false,
      timestamp: item.timestamp,
    };

    revisionsExtras.unshift(extras);
  }

  return revisionsExtras;
};

// Generate configuration title in format sourceTankGroup - sourceTankGroupTags - newTag - targetTank
export const generateConfigTitle = (configuration: VaccineConfiguration | ChallengeConfiguration) => {
  // Create a Set to track unique tag titles
  const uniqueTagTitles = new Set();

  // Process source group tags first
  const sourceTags = configuration.sourceGroup?.tags
    ?.filter(tag => tag.title !== "Start Tag")
    ?.map((tag: any) => {
      // If we've already seen this tag title, skip it
      if (uniqueTagTitles.has(tag.title)) {
        return null;
      }

      // Add this tag title to our set
      uniqueTagTitles.add(tag.title);

      return `
        <span style="display: inline-block; margin: 2px; font-weight: bold; padding: 2px 5px; font-size: 10px; text-align: center; color: #${removeLeadingHash(
        tag.textColor
      )}; border-radius: 5px; background-color: #${removeLeadingHash(
        tag.backgroundColor
      )};">${tag.title}</span>`;
    })
    ?.filter(Boolean) // Remove null entries
    ?.join(" ") || "";

  // Then process destination group tags
  const tagsInSection = configuration.destinationGroup
    ? configuration.destinationGroup.tags
    ?.filter(tag => tag.title !== "Start Tag")
    ?.map((tag) => {
      // If we've already seen this tag title, skip it
      if (uniqueTagTitles.has(tag.title)) {
        return null;
      }

      // Add this tag title to our set
      uniqueTagTitles.add(tag.title);

      return `
        <span style="display: inline-block; margin: 2px; font-weight: bold; padding: 2px 5px; font-size: 10px; text-align: center; color: #${removeLeadingHash(
        tag.textColor
      )}; border-radius: 5px; background-color: #${removeLeadingHash(
        tag.backgroundColor
      )};">${tag.title}</span>`;
    })
    ?.filter(Boolean) // Remove null entries
    ?.join(" ") || ""
    : "";

  return `${
    configuration.destinationTank?.label ?? configuration.destinationTank?.name
  } - ${sourceTags} ${tagsInSection}`;
};

// Prepare dilutions table data
export const prepareDilutionsTableData = (dilutions: Dilution[]): Row[] => {
  const preparedData: Row[] = dilutions.map((p) => {
    return {
      ratio: `<textarea class="resize-none h-5 bg-transparent" data-row-id="${p.key}" data-field="ratio" @input="handleTextareaInput">${p.ratio}</textarea>`,
      volumeAdded: `<textarea class="resize-none h-5 bg-transparent">${p.volumeAdded}</textarea>`,
      pbsVolume: `<textarea class="resize-none h-5 bg-transparent">${p.pbsVolume}</textarea>`,
      resultingDose: `<textarea class="resize-none h-5 bg-transparent">${p.resultingDose}</textarea>`,
      toBeUsedInChallenge: createSelect(p.toBeUsedInChallenge || false),
      completed: p.completed,
      shouldColorRow: p.completed,
    };
  });

  return preparedData;
};

// Create empty dilution row
export const createEmptyDilutionRow = (): Dilution => {
  return {
    key: "",
    ratio: "",
    volumeAdded: "",
    pbsVolume: 0,
    resultingDose: "",
    toBeUsedInChallenge: false,
    completed: 1,
  };
};

// Create simple yes/no select element
function createSelect(selectedOption: boolean): string {
  return `
    <select class="w-full bg-transparent">
      <option value="1" ${selectedOption ? "selected" : ""}>Yes</option>
      <option value="2" ${!selectedOption ? "selected" : ""}>No</option>
    </select>
  `;
}

// Debounce update task usually tied to input fields
// export const debouncedUpdateToIndexDB = debounce((task) => {
//   if (task) {
//     TasksService.updateSingleTaskInIndexDB(task);
//   }
// }, 500);

// Change status for entry on row click
// export const changeTableRowStatus = (row: Row, rowDataType: string) => {
//   const TasksStore = useTasksStore();
//   // TODO: We will use this globally, so more conditions to come
//   if (
//     TasksStore.activeTask &&
//     isPreChallengeTask(TasksStore.activeTask) &&
//     rowDataType === "dilution"
//   ) {
//     const dilutionRow = TasksStore.activeTask?.details?.dilutions.find(
//       (d: any) => d.id === row.id
//     );

//     if (dilutionRow !== undefined) {
//       dilutionRow.completed =
//         dilutionRow.completed === 3
//           ? 1
//           : ((dilutionRow.completed + 1) as 1 | 2 | 3);
//       debouncedUpdateToIndexDB(TasksStore.activeTask);
//     }
//   }
// };

// Convert snake to camel case
export const convertSnakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_match, group) => group.toUpperCase());
};

// Convert object keys to camel case
export const convertObjectKeysToCamel = (obj: any): any => {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => convertObjectKeysToCamel(item));
  }

  const camelCaseObj: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = convertSnakeToCamel(key);
      camelCaseObj[camelKey] = convertObjectKeysToCamel(obj[key]);
    }
  }
  return camelCaseObj;
};

// Convert camel case to snake case
export const convertCamelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

// Convert object keys to snake case
export const convertObjectKeysToSnake = (obj: any): any => {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => convertObjectKeysToSnake(item));
  }

  const snakeCaseObj: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeKey = convertCamelToSnake(key);
      snakeCaseObj[snakeKey] = convertObjectKeysToSnake(obj[key]);
    }
  }
  return snakeCaseObj;
};

export const flattenDetails = (task: Task): unknown => {
  // Handle environmental tasks
  if (isEnvironmentalTask(task) && task.details?.tanks) {
    const convertedTanks: Tank[] = task.details.tanks.map((tank: any) => {
      if (tank.measures) {
        const convertedMeasures = {} as TankMeasures;

        for (const measureKey in tank.measures) {
          if (Object.prototype.hasOwnProperty.call(tank.measures, measureKey)) {
            const originalMeasure = tank.measures[measureKey];
            convertedMeasures[measureKey] = {
              value:
                convertToDouble(originalMeasure.value)?.toString() ||
                originalMeasure.value,
              unit: originalMeasure.unit,
            };
          }
        }

        return Object.assign({}, tank, { measures: convertedMeasures }) as Tank;
      }

      return tank as Tank;
    });

    task = { ...task, details: { ...task.details, tanks: convertedTanks } };
  }

  // Special handling for fish_import tasks
  if (task.objective.id === "fish_import") {
    console.log((task.details as any).imports);
  }

  // Environmental modifier: send only tank keys + user-entered attribute values
  if (task.objective.id === "environmental_modifier") {
    const details = (task as any).details;
    const tanks = (details?.tanks ?? []).map((tank: any) => ({
      key: tank.key,
      attributes: (tank.attributes ?? []).map((attr: any) => {
        const entry: any = { attribute_id: attr.attributeId };
        if (attr.name === 'light_regime') {
          entry.light = attr.enteredValue?.light ?? attr.target?.light ?? null;
          entry.dark  = attr.enteredValue?.dark  ?? attr.target?.dark  ?? null;
        } else {
          entry.value = attr.enteredValue?.value ?? null;
          entry.unit  = attr.enteredValue?.unit ?? attr.unit ?? null;
        }
        return entry;
      }),
    }));
    return { tanks };
  }

  // Standard processing for other task types
  const { details, ...rest } = task;

  if (details) {
    return {...rest, ...details};
  }

  return task;
}

// Convert value to double
export const convertToDouble = (
  value: string | undefined
): number | undefined => {
  if (value !== undefined) {
    const parsedValue = parseFloat(value);
    return isNaN(parsedValue) ? undefined : parsedValue;
  }
  return undefined;
};
